import { Stack, SxProps, Typography } from '@mui/material';
import { Paths } from 'services/navigation/paths';
import { useNavigate } from 'react-router-dom';
import { TableV2 } from 'components/Table/TableV2';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import React, { useMemo, useState } from 'react';
import { Button } from 'system/atoms';
import { Header } from 'common/ui/Header';
import { SearchInput } from 'common/ui';
import { useGetCandidateBatchGroupByOrganizationQuery } from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { useTableSort } from 'common/hooks/useTableSort';
import { SearchableSortDirection } from 'generated-graphql/types';
import { RulesetItem } from 'modules/analyse-rulesets/types';
import { RulesetSortField } from 'modules/analyse-rulesets/constants';
import { getAnalyseRulesetsColumn } from 'modules/analyse-rulesets/utils/getAnalyseRulesetsColumn';

const container: SxProps = {
  p: 3
};

export const AnalyseRulesetsPage = () => {
  const navigate = useNavigate();
  const organization = useOrganization();

  const [search, setSearch] = useState('');

  const { sort, handleSort } = useTableSort<RulesetSortField, RulesetItem>({
    field: RulesetSortField.UPDATED_AT,
    direction: SearchableSortDirection.Asc
  });

  const { data, loading } = useGetCandidateBatchGroupByOrganizationQuery({
    variables: {
      organizationId: organization.id,
      limit: 1000
    },
    skip: !organization.id
  });

  const list = useMemo<RulesetItem[]>(() => {
    if (!data) {
      return [];
    }

    return extractQueryListItems(data) as RulesetItem[];
  }, [data]);

  const filteredGroupList = useMemo<RulesetItem[]>(() => {
    if (!list.length) {
      return [];
    }

    if (!search.length) {
      return list;
    }

    return list.filter((template) =>
      template.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [list, search]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <Stack direction="column" spacing={2} sx={container}>
      <Header
        breadcrumbs={[{ name: 'Analyse', path: Paths.ANALYSE }]}
        currentBreadcrumb="Rulesets"
        title="Rulesets"
        items={['Reusable rules for use across analyses']}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SearchInput
          sx={{ width: 320 }}
          placeholder="Search rulesets"
          onChange={handleSearchChange}
        />
        <Button
          variant="primary"
          onClick={() => navigate(Paths.ANALYSE_RULESET_CREATE)}
        >
          Create ruleset
        </Button>
      </Stack>

      <TableV2
        data={filteredGroupList}
        isLoading={loading}
        columns={getAnalyseRulesetsColumn()}
        sortServer={false}
        pagination
        onSort={handleSort}
        defaultSortFieldId={sort.field}
        defaultSortAsc={sort.direction === SearchableSortDirection.Asc}
        emptyStateComponent={
          <Stack direction="column" alignItems="center" spacing={3}>
            <Typography fontSize="20px" fontWeight="600">
              No rulesets available
            </Typography>
            <Button
              variant="primary"
              onClick={() => navigate(Paths.ANALYSE_RULESET_CREATE)}
            >
              Create your first one
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
};
